import { useFormik } from "formik";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import makeObservable from "./makeObservable";
import { hasSearchCriteria } from "./search/searchCriteria";
import { removeSearchSynopsisTerm } from "./search/searchSynopsis";
import { buildNavigationUrl } from "./buildNavigationUrl";

export const SEARCH_RESULTS_LABEL = "Search Results";

const moduleSearchResets = new Map();
const moduleFilterFormStores = new Map();
const getModulePath = pathname => pathname?.split("/").filter(Boolean)[0];
const noSubscription = () => () => {};

const isPlainSearchData = value =>
  value !== null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  !(value instanceof Date);

// Snapshots passed to `set` must not keep the live object's nested fields.
// Search data is keyed by field name (`name`, `type`, …), so a shared nested
// object would show one module's value in another module with the same field.
const cloneSearchData = value => {
  if (Array.isArray(value)) return value.map(cloneSearchData);
  if (!isPlainSearchData(value)) return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, nested]) => [key, cloneSearchData(nested)]),
  );
};

// Remove every field from a live search bag. Replacing the store is not enough
// when another module still holds the bag, or when a field was added in place:
// `name` and any other shared key would still be there after the switch.
// Nested values are left alone so a filter that aliases a form field does not
// wipe that field's default operator.
const blankSearchFields = value => {
  if (!isPlainSearchData(value)) return;
  Object.keys(value).forEach(key => {
    delete value[key];
  });
};

// Raw UI values deliberately live separately from the lossy backend filter stores.
// `searchLivesInUrl` modules (Contacts) serialise the search into the list
// query string; the last one is remembered so links that drop it can return.
// `toFilter` converts form values to the backend filter so the search can be
// edited (e.g. a synopsis tag removed) while its search form is closed.
export const createFilterFormStore = (
  modulePath,
  filterStore,
  navigationStore,
  { searchLivesInUrl = false, toFilter } = {},
) => {
  const formStore = makeObservable(undefined);
  const searchSubmittedStore = makeObservable(false);
  const defaultValuesStore = makeObservable(undefined);
  const searchLocationStore = makeObservable(undefined);
  const store = {
    ...formStore,
    defaultValuesStore,
    filterStore,
    searchLivesInUrl,
    searchLocationStore,
    searchSubmittedStore,
    toFilter,
  };
  if (modulePath) {
    const initialFilter = cloneSearchData(filterStore.get());
    const initialNavigation = cloneSearchData(navigationStore.get());
    const clearSearchFields = () => {
      const formValues = formStore.get();
      if (formValues && formValues !== defaultValuesStore.get()) {
        blankSearchFields(formValues);
      }
      blankSearchFields(filterStore.get());
    };
    moduleFilterFormStores.set(modulePath, store);
    moduleSearchResets.set(modulePath, () => {
      clearSearchFields();
      formStore.set(undefined);
      searchSubmittedStore.set(false);
      searchLocationStore.set(undefined);
      filterStore.set(cloneSearchData(initialFilter));
      navigationStore.set(cloneSearchData(initialNavigation));
    });
  }
  return store;
};

export const resetModuleSearch = pathname => {
  moduleSearchResets.get(getModulePath(pathname))?.();
};

const hasFormSearchCriteria = store =>
  hasSearchCriteria(store.get(), store.defaultValuesStore.get());

export const markModuleSearchSubmitted = pathname => {
  const store = moduleFilterFormStores.get(getModulePath(pathname));
  store?.searchSubmittedStore.set(hasFormSearchCriteria(store));
};

export const setModuleSearchLocation = (pathname, locationSearch) => {
  moduleFilterFormStores
    .get(getModulePath(pathname))
    ?.searchLocationStore.set(locationSearch || undefined);
};

// Takes one synopsis term (or one of its values) out of the module's
// remembered form values and, when the module can convert them, its filter.
export const removeModuleSearchTerm = (pathname, removal) => {
  const store = moduleFilterFormStores.get(getModulePath(pathname));
  const defaults = store?.defaultValuesStore.get();
  const values = store?.get() ?? defaults;
  if (!values) return;
  const nextValues = removeSearchSynopsisTerm(values, removal, defaults);
  if (store.toFilter) store.filterStore.set(store.toFilter(nextValues));
  store.set(nextValues);
};

export const getModuleSearchResultsUrl = (pathname, searchLocation) =>
  buildNavigationUrl(`/${getModulePath(pathname)}`, searchLocation || "");

const hasSubmittedSearchCriteria = store => {
  if (!store) return false;
  if (store.searchLivesInUrl) return Boolean(store.searchLocationStore.get());
  return store.searchSubmittedStore.get() && hasFormSearchCriteria(store);
};

const subscribeToSearchState = store => listener => {
  const unsubscribes = [
    store.subscribe(listener),
    store.searchSubmittedStore.subscribe(listener),
    store.defaultValuesStore.subscribe(listener),
    store.searchLocationStore.subscribe(listener),
  ];
  return () => unsubscribes.forEach(unsubscribe => unsubscribe());
};

export const useModuleSearchLocation = pathname => {
  const store = moduleFilterFormStores.get(getModulePath(pathname));
  return useSyncExternalStore(
    store?.searchLocationStore.subscribe ?? noSubscription,
    () => store?.searchLocationStore.get(),
  );
};

// True only after a search was submitted and while the form still holds
// criteria that differ from its defaults (or, for URL-based searches, while a
// search query string is remembered).
export const useModuleSearchSubmitted = pathname => {
  const store = moduleFilterFormStores.get(getModulePath(pathname));
  const subscribe = useMemo(
    () => (store ? subscribeToSearchState(store) : noSubscription),
    [store],
  );
  return useSyncExternalStore(subscribe, () =>
    hasSubmittedSearchCriteria(store),
  );
};

export const getSearchAwareLabel = (label, hasSearchResults) =>
  hasSearchResults ? SEARCH_RESULTS_LABEL : label;

// Auth stays mounted across navigations, including opening a record inside the
// same module. Reset when this module path is left, which is the cleanup of
// the effect below, and again if Auth itself unmounts.
export const useResetSearchOnModuleExit = pathname => {
  const modulePath = getModulePath(pathname);
  useEffect(() => () => {
    console.log("resetModuleSearch", modulePath);
    resetModuleSearch(modulePath);
  }, [modulePath]);
};

export const usePersistedFilterFormik = (options, store) => {
  const [defaults] = useState(() => options.initialValues);
  const [initialValues] = useState(() => store.get() ?? defaults);
  const formik = useFormik({
    ...options,
    initialValues,
    enableReinitialize: false,
  });

  useEffect(() => {
    store.defaultValuesStore?.set(defaults);
  }, [store, defaults]);

  useEffect(() => {
    store.set(formik.values);
  }, [store, formik.values]);

  const { resetForm: resetFormikForm } = formik;
  const resetForm = useCallback(
    nextState => {
      // Search buttons pass click events; only explicit Formik state overrides
      // should replace the original defaults when resetting a reopened form.
      resetFormikForm(nextState?.values ? nextState : { values: defaults });
      store.searchSubmittedStore?.set(false);
    },
    [defaults, resetFormikForm, store],
  );

  const valuesRef = useRef(formik.values);
  valuesRef.current = formik.values;
  const { setValues } = formik;

  // Values set from outside the form (a removed synopsis tag) must reach a
  // mounted form, or its next edit would write the stale values back.
  useEffect(
    () =>
      store.subscribe(values => {
        if (values === undefined) resetForm();
        else if (values !== valuesRef.current) setValues(values, false);
      }),
    [store, resetForm, setValues],
  );

  return { ...formik, resetForm };
};
